import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSpinner,
  FaCheck,
  FaGlobe,
  FaLink,
  FaExternalLinkAlt,
  FaCopy,
  FaCheckCircle,
  FaExclamationTriangle,
  FaShieldAlt,
  FaRocket,
  FaCalendarAlt,
  FaInfoCircle,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";

const ResultURLManager = () => {
  const [urls, setUrls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUrl, setEditingUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    description: "",
    examType: "BE",
    urlType: "Regular",
    isDefault: false,
  });
  const [testingUrl, setTestingUrl] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const urlTypes = ["Regular", "Revaluation", "Both"];
  const examTypes = ["BE", "MTech", "MBA", "All"];

  useEffect(() => {
    checkDarkMode();
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  useEffect(() => {
    fetchUrls();
  }, []);

  const fetchUrls = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        "/api/results/admin/urls",
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setUrls(response.data);
    } catch (error) {
      toast.error("Failed to fetch URLs");
    } finally {
      setLoading(false);
    }
  };

  const handleTestUrl = async (url) => {
    setTestingUrl(url);
    setTestResult(null);
    try {
      const response = await axios.get(
        `/api/results/test-vtu-url?url=${encodeURIComponent(url)}`,
      );
      setTestResult(response.data);
      if (response.data.isWorking) {
        toast.success("URL is working!");
      } else {
        toast.error("URL is not working");
      }
    } catch (error) {
      setTestResult({ isWorking: false, error: error.message });
      toast.error("Failed to test URL");
    } finally {
      setTestingUrl(null);
    }
  };

  const handleSaveUrl = async () => {
    if (!formData.name || !formData.url) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (editingUrl) {
        await axios.put(
          `/api/results/admin/urls/${editingUrl._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("URL updated successfully");
      } else {
        await axios.post(
          "/api/results/admin/urls",
          formData,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        toast.success("URL added successfully");
      }
      fetchUrls();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save URL");
    }
  };

  const handleDeleteUrl = async (urlEntry) => {
    if (
      !window.confirm(
        `Delete "${urlEntry.name}"? This will remove this URL from the system.`,
      )
    )
      return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `/api/results/admin/urls/${urlEntry._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("URL deleted successfully");
      fetchUrls();
    } catch (error) {
      toast.error("Failed to delete URL");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      url: "",
      description: "",
      examType: "BE",
      urlType: "Regular",
      isDefault: false,
    });
    setEditingUrl(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getUrlTypeBadge = (type) => {
    const badges = {
      Regular: {
        bg: "linear-gradient(135deg, #06b6d4, #0891b2)",
        text: "Regular",
      },
      Revaluation: {
        bg: "linear-gradient(135deg, #f59e0b, #d97706)",
        text: "Revaluation",
      },
      Both: { bg: "linear-gradient(135deg, #8b5cf6, #6d28d9)", text: "Both" },
    };
    const badge = badges[type] || badges.Regular;
    return (
      <span
        style={{
          background: badge.bg,
          color: "white",
          padding: "0.2rem 0.7rem",
          borderRadius: "20px",
          fontSize: "0.65rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
        }}
      >
        {badge.text}
      </span>
    );
  };

  const getDefaultBadge = (isDefault, urlType) => {
    if (!isDefault) return null;
    return (
      <span
        style={{
          background: "linear-gradient(135deg, #10b981, #059669)",
          color: "white",
          padding: "0.2rem 0.7rem",
          borderRadius: "20px",
          fontSize: "0.65rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.3rem",
        }}
      >
        <FaCheck size={10} /> Default {urlType}
      </span>
    );
  };

  // Helper functions for styling
  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 300, damping: 24 },
    },
  };

  const regularUrls = urls.filter(
    (u) => u.urlType === "Regular" || u.urlType === "Both",
  );
  const revaluationUrls = urls.filter(
    (u) => u.urlType === "Revaluation" || u.urlType === "Both",
  );

  function renderUrlList(urlsList, currentType, sectionColor) {
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "40px" }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          >
            <FaSpinner size={30} color="#8b5cf6" />
          </motion.div>
        </div>
      );
    }

    if (urlsList.length === 0) {
      return (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: getCardBackground(),
            backdropFilter: "blur(20px)",
            borderRadius: "20px",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              width: "60px",
              height: "60px",
              background: isDarkMode
                ? "rgba(139,92,246,0.1)"
                : "rgba(139,92,246,0.05)",
              borderRadius: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
            }}
          >
            <FaGlobe size={30} color="#8b5cf6" />
          </div>
          <p style={{ color: getTextColor(), marginTop: "16px" }}>
            No {currentType} URLs configured.
          </p>
          <p style={{ color: getTextSecondary(), fontSize: "13px" }}>
            Click "Add New URL" to configure {currentType.toLowerCase()} results
            portal URLs.
          </p>
        </div>
      );
    }

    return (
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{ display: "grid", gap: "16px" }}
      >
        {urlsList.map((url, idx) => (
          <motion.div
            key={url._id}
            variants={itemVariants}
            whileHover={{ y: -2 }}
            style={{
              background: getCardBackground(),
              backdropFilter: "blur(20px)",
              borderRadius: "20px",
              padding: "20px",
              border: `1px solid ${url.isDefault ? "#8b5cf6" : getBorderColor()}`,
              boxShadow: url.isDefault
                ? "0 4px 20px rgba(139,92,246,0.15)"
                : "none",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                    marginBottom: "12px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color: getTextColor(),
                      margin: 0,
                    }}
                  >
                    {url.name}
                  </h3>
                  {getUrlTypeBadge(url.urlType)}
                  {getDefaultBadge(url.isDefault, url.urlType)}
                  {!url.isActive && (
                    <span
                      style={{
                        background: "linear-gradient(135deg, #ef4444, #dc2626)",
                        color: "white",
                        padding: "0.2rem 0.7rem",
                        borderRadius: "20px",
                        fontSize: "0.65rem",
                      }}
                    >
                      INACTIVE
                    </span>
                  )}
                  <span
                    style={{
                      background: isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "#f0f0f0",
                      color: getTextSecondary(),
                      padding: "0.2rem 0.7rem",
                      borderRadius: "20px",
                      fontSize: "0.65rem",
                    }}
                  >
                    {url.examType}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                    background: isDarkMode
                      ? "rgba(139,92,246,0.05)"
                      : "#f8f9fa",
                    padding: "10px 12px",
                    borderRadius: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <FaLink style={{ color: "#8b5cf6", fontSize: "12px" }} />
                  <code
                    style={{
                      fontSize: "12px",
                      color: getTextSecondary(),
                      flex: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {url.url}
                  </code>
                  <button
                    onClick={() => copyToClipboard(url.url)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#8b5cf6",
                      padding: "6px",
                      borderRadius: "6px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "#f0f0f0")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                    title="Copy URL"
                  >
                    <FaCopy size={12} />
                  </button>
                  <button
                    onClick={() => handleTestUrl(url.url)}
                    disabled={testingUrl === url.url}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#06b6d4",
                      padding: "6px",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = isDarkMode
                        ? "rgba(139,92,246,0.1)"
                        : "#f0f0f0")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                    title="Test URL"
                  >
                    {testingUrl === url.url ? (
                      <FaSpinner
                        size={12}
                        style={{ animation: "spin 1s linear infinite" }}
                      />
                    ) : (
                      <FaExternalLinkAlt size={12} />
                    )}
                    Test
                  </button>
                </div>
                {testResult && testingUrl === url.url && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "10px 12px",
                      borderRadius: "10px",
                      background: testResult.isWorking
                        ? "rgba(16,185,129,0.1)"
                        : "rgba(239,68,68,0.1)",
                      border: `1px solid ${testResult.isWorking ? "#10b981" : "#ef4444"}`,
                      fontSize: "12px",
                    }}
                  >
                    {testResult.isWorking ? (
                      <>
                        <FaCheckCircle
                          style={{ marginRight: "6px", color: "#10b981" }}
                        />
                        <span style={{ color: "#10b981" }}>
                          URL is working!
                        </span>
                      </>
                    ) : (
                      <>
                        <FaExclamationTriangle
                          style={{ marginRight: "6px", color: "#ef4444" }}
                        />
                        <span style={{ color: "#ef4444" }}>
                          Error: {testResult.error}
                        </span>
                      </>
                    )}
                  </div>
                )}
                {url.description && (
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: "12px",
                      marginTop: "8px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <FaInfoCircle size={10} color="#8b5cf6" />
                    {url.description}
                  </p>
                )}
                <p
                  style={{
                    color: getTextSecondary(),
                    fontSize: "10px",
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <FaCalendarAlt size={10} />
                  Added: {new Date(url.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {!url.isDefault && url.urlType === currentType && (
                  <button
                    onClick={() => {
                      setFormData({
                        name: url.name,
                        url: url.url,
                        description: url.description || "",
                        examType: url.examType,
                        urlType: url.urlType,
                        isDefault: true,
                      });
                      setEditingUrl(url);
                      setShowModal(true);
                    }}
                    style={{
                      padding: "6px 12px",
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.opacity = "0.9")
                    }
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                    title="Set as Default"
                  >
                    <FaCheck size={10} /> Set Default
                  </button>
                )}
                <button
                  onClick={() => {
                    setFormData({
                      name: url.name,
                      url: url.url,
                      description: url.description || "",
                      examType: url.examType,
                      urlType: url.urlType,
                      isDefault: url.isDefault,
                    });
                    setEditingUrl(url);
                    setShowModal(true);
                  }}
                  style={{
                    padding: "6px 12px",
                    background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <FaEdit size={10} /> Edit
                </button>
                <button
                  onClick={() => handleDeleteUrl(url)}
                  style={{
                    padding: "6px 12px",
                    background: "linear-gradient(135deg, #ef4444, #dc2626)",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "11px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <FaTrash size={10} /> Delete
                </button>
              </div>
            </div>
          </motion.div>
        ))}
      </motion.div>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaGlobe size={20} color="white" />
            </div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: "bold",
                color: getTextColor(),
                margin: 0,
              }}
            >
              VTU Result URLs
            </h2>
          </div>
          <p
            style={{
              color: getTextSecondary(),
              fontSize: "0.85rem",
              marginLeft: "50px",
            }}
          >
            Manage separate URLs for Regular and Revaluation results. The system
            will use the appropriate URL based on user selection.
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          style={{
            padding: "10px 20px",
            background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "600",
            boxShadow: "0 4px 14px 0 rgba(139,92,246,0.4)",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          <FaPlus /> Add New URL
        </button>
      </div>

      {/* Legend Section */}
      <div
        style={{
          background: isDarkMode ? "rgba(139,92,246,0.1)" : "#fff5e6",
          borderRadius: "16px",
          padding: "16px",
          marginBottom: "24px",
          border: `1px solid ${getBorderColor()}`,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                background: "#06b6d4",
                borderRadius: "2px",
              }}
            ></div>
            <span style={{ fontSize: "13px", color: getTextSecondary() }}>
              Regular Results URL
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                background: "#f59e0b",
                borderRadius: "2px",
              }}
            ></div>
            <span style={{ fontSize: "13px", color: getTextSecondary() }}>
              Revaluation Results URL
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                background: "#8b5cf6",
                borderRadius: "2px",
              }}
            ></div>
            <span style={{ fontSize: "13px", color: getTextSecondary() }}>
              Works for Both
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FaCheck color="#10b981" size={12} />
            <span style={{ fontSize: "13px", color: getTextSecondary() }}>
              Default URL for that type
            </span>
          </div>
        </div>
      </div>

      {/* Regular Results Section */}
      <div style={{ marginBottom: "32px" }}>
        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "#06b6d4",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              background: "rgba(6,182,212,0.1)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaLink size={14} color="#06b6d4" />
          </div>
          Regular Results URLs
        </h3>
        {renderUrlList(regularUrls, "Regular", "#06b6d4")}
      </div>

      {/* Revaluation Results Section */}
      <div style={{ marginBottom: "32px" }}>
        <h3
          style={{
            fontSize: "1.2rem",
            fontWeight: "bold",
            color: "#f59e0b",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              background: "rgba(245,158,11,0.1)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FaLink size={14} color="#f59e0b" />
          </div>
          Revaluation Results URLs
        </h3>
        {renderUrlList(revaluationUrls, "Revaluation", "#f59e0b")}
      </div>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.5)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: "spring", damping: 25 }}
              style={{
                background: getCardBackground(),
                backdropFilter: "blur(20px)",
                borderRadius: "24px",
                maxWidth: "550px",
                width: "100%",
                padding: "28px",
                maxHeight: "90vh",
                overflow: "auto",
                border: `1px solid ${getBorderColor()}`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {editingUrl ? (
                      <FaEdit size={16} color="white" />
                    ) : (
                      <FaPlus size={16} color="white" />
                    )}
                  </div>
                  <h2
                    style={{
                      fontSize: "1.3rem",
                      fontWeight: "bold",
                      color: getTextColor(),
                    }}
                  >
                    {editingUrl ? "Edit URL" : "Add New URL"}
                  </h2>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)",
                    border: "none",
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    color: getTextSecondary(),
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.2)"
                      : "rgba(139,92,246,0.1)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = isDarkMode
                      ? "rgba(139,92,246,0.1)"
                      : "rgba(139,92,246,0.05)")
                  }
                >
                  <FaTimes />
                </button>
              </div>

              {/* Name */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="e.g., VTU Results Dec 2025"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
              </div>

              {/* URL */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  URL *
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) =>
                    setFormData({ ...formData, url: e.target.value })
                  }
                  placeholder="https://results.vtu.ac.in/..."
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "14px",
                    outline: "none",
                    background: getInputBackground(),
                    color: getTextColor(),
                    transition: "all 0.3s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#8b5cf6")}
                  onBlur={(e) =>
                    (e.target.style.borderColor = getBorderColor())
                  }
                />
                <button
                  onClick={() => handleTestUrl(formData.url)}
                  disabled={testingUrl === formData.url}
                  style={{
                    marginTop: "8px",
                    padding: "6px 12px",
                    background: isDarkMode ? "rgba(139,92,246,0.1)" : "#f8f9fa",
                    border: `1px solid ${getBorderColor()}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    color: getTextColor(),
                  }}
                >
                  {testingUrl === formData.url ? (
                    <FaSpinner
                      style={{ animation: "spin 1s linear infinite" }}
                    />
                  ) : (
                    <FaExternalLinkAlt />
                  )}
                  Test URL
                </button>
              </div>

              {/* Exam Type and URL Type */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    Exam Type
                  </label>
                  <select
                    value={formData.examType}
                    onChange={(e) =>
                      setFormData({ ...formData, examType: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    {examTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      color: getTextSecondary(),
                      fontWeight: "500",
                      fontSize: "0.85rem",
                    }}
                  >
                    URL Type *
                  </label>
                  <select
                    value={formData.urlType}
                    onChange={(e) =>
                      setFormData({ ...formData, urlType: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${getBorderColor()}`,
                      borderRadius: "10px",
                      fontSize: "14px",
                      outline: "none",
                      background: getInputBackground(),
                      color: getTextColor(),
                    }}
                  >
                    {urlTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <p
                    style={{
                      fontSize: "10px",
                      color: getTextSecondary(),
                      marginTop: "4px",
                    }}
                  >
                    {formData.urlType === "Regular"
                      ? 'Used when user selects "Regular" exam type'
                      : formData.urlType === "Revaluation"
                        ? 'Used when user selects "Revaluation" exam type'
                        : "Works for both Regular and Revaluation results"}
                  </p>
                </div>
              </div>

              {/* Default Checkbox */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.isDefault}
                    onChange={(e) =>
                      setFormData({ ...formData, isDefault: e.target.checked })
                    }
                    style={{
                      width: "16px",
                      height: "16px",
                      accentColor: "#8b5cf6",
                    }}
                  />
                  <span style={{ color: getTextColor(), fontSize: "0.85rem" }}>
                    Set as Default URL for{" "}
                    {formData.urlType === "Both" ? "all" : formData.urlType}{" "}
                    results
                  </span>
                </label>
                <p
                  style={{
                    fontSize: "10px",
                    color: getTextSecondary(),
                    marginTop: "4px",
                    marginLeft: "24px",
                  }}
                >
                  Only one default URL per exam type and URL type can exist.
                </p>
              </div>

              {/* Description */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "6px",
                    color: getTextSecondary(),
                    fontWeight: "500",
                    fontSize: "0.85rem",
                  }}
                >
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Add description..."
                  rows="2"
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    fontSize: "14px",
                    outline: "none",
                    resize: "vertical",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>

              {/* Test Result */}
              {testResult && testingUrl === formData.url && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    borderRadius: "10px",
                    background: testResult.isWorking
                      ? "rgba(16,185,129,0.1)"
                      : "rgba(239,68,68,0.1)",
                    border: `1px solid ${testResult.isWorking ? "#10b981" : "#ef4444"}`,
                    fontSize: "12px",
                  }}
                >
                  {testResult.isWorking ? (
                    <>
                      <FaCheckCircle
                        style={{ marginRight: "6px", color: "#10b981" }}
                      />
                      <span style={{ color: "#10b981" }}>URL is working!</span>
                    </>
                  ) : (
                    <>
                      <FaExclamationTriangle
                        style={{ marginRight: "6px", color: "#ef4444" }}
                      />
                      <span style={{ color: "#ef4444" }}>
                        Error: {testResult.error}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Buttons */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: isDarkMode
                      ? "rgba(100,116,139,0.3)"
                      : "#f8f9fa",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "500",
                    color: getTextColor(),
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.8")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUrl}
                  style={{
                    flex: 1,
                    padding: "10px",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  <FaSave style={{ marginRight: "6px" }} />
                  {editingUrl ? "Update" : "Add"} URL
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default ResultURLManager;

